import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, RotateCcw } from "lucide-react";

/**
 * Live front-camera selfie only (no gallery upload).
 * Calls onCapture(File) after the user takes a photo.
 */
export default function LiveSelfieCapture({
  onCapture,
  busy = false,
  capturedLabel = "Selfie ready",
  testIdPrefix = "live-selfie",
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [camOn, setCamOn] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [err, setErr] = useState("");
  const [capturing, setCapturing] = useState(false);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOn(false);
  }

  useEffect(() => () => stopCamera(), []);

  async function openCamera() {
    setErr("");
    setPreviewUrl("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Camera is not supported in this browser. Try Chrome or Safari on your phone.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: 720 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setCamOn(true);
      setTimeout(async () => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch {
            /* ignore autoplay race */
          }
        }
      }, 50);
    } catch {
      setErr("Camera access denied. Allow camera permission and try again.");
    }
  }

  async function capture() {
    setErr("");
    setCapturing(true);
    try {
      const v = videoRef.current;
      const c = canvasRef.current;
      if (!v || !c || !v.videoWidth) {
        setErr("Camera not ready — wait a moment and try again.");
        return;
      }
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      const ctx = c.getContext("2d");
      // Mirror so the preview matches what the user saw
      ctx.translate(c.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(v, 0, 0, c.width, c.height);
      const blob = await new Promise((resolve, reject) => {
        c.toBlob((b) => (b ? resolve(b) : reject(new Error("capture failed"))), "image/jpeg", 0.92);
      });
      const file = new File([blob], `live-selfie-${Date.now()}.jpg`, { type: "image/jpeg" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      stopCamera();
      onCapture?.(file);
    } catch {
      setErr("Could not capture. Please try again.");
    } finally {
      setCapturing(false);
    }
  }

  function retake() {
    setPreviewUrl("");
    onCapture?.(null);
    openCamera();
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-root`}>
      <canvas ref={canvasRef} className="hidden" />
      <div className="relative mx-auto w-full max-w-sm aspect-square overflow-hidden rounded-2xl border border-border bg-surface-2">
        {camOn && !previewUrl ? (
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="h-full w-full object-cover scale-x-[-1]"
            data-testid={`${testIdPrefix}-video`}
          />
        ) : null}
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Captured selfie"
            className="h-full w-full object-cover"
            data-testid={`${testIdPrefix}-preview`}
          />
        ) : null}
        {!camOn && !previewUrl ? (
          <div className="absolute inset-0 grid place-items-center text-muted-foreground">
            <div className="text-center space-y-2 px-4">
              <Camera size={36} className="mx-auto opacity-70" />
              <p className="text-[13px]">Open your camera for a live selfie</p>
            </div>
          </div>
        ) : null}
        {previewUrl ? (
          <div className="absolute bottom-3 left-3 rounded-full bg-up/90 px-3 py-1 text-[11px] font-semibold text-white">
            {capturedLabel}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {!camOn && !previewUrl ? (
          <button
            type="button"
            onClick={openCamera}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-background disabled:opacity-40"
            data-testid={`${testIdPrefix}-open`}
          >
            <Camera size={16} /> Open camera
          </button>
        ) : null}
        {camOn && !previewUrl ? (
          <>
            <button
              type="button"
              onClick={capture}
              disabled={busy || capturing}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-background disabled:opacity-40"
              data-testid={`${testIdPrefix}-capture`}
            >
              {capturing ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
              Capture selfie
            </button>
            <button
              type="button"
              onClick={stopCamera}
              className="text-[13px] font-semibold text-muted-foreground hover:text-foreground"
              data-testid={`${testIdPrefix}-cancel`}
            >
              Cancel
            </button>
          </>
        ) : null}
        {previewUrl ? (
          <button
            type="button"
            onClick={retake}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline disabled:opacity-40"
            data-testid={`${testIdPrefix}-retake`}
          >
            <RotateCcw size={14} /> Retake
          </button>
        ) : null}
      </div>

      {err ? <p className="text-[12px] text-down" data-testid={`${testIdPrefix}-error`}>{err}</p> : null}
      <p className="text-[12px] text-muted-foreground">
        Face the camera in good light. Gallery upload is not allowed — live capture only.
      </p>
    </div>
  );
}
